/**
 * Security Phrase Service
 * Handles security phrase hashing, validation, token generation, and rate limiting
 */

import { getFailoverAwareDatabase } from '../config/firebaseWithFailover';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import Logger from './logger';

const MAX_ATTEMPTS = 6;
const LOCKOUT_DURATION_MS = 60 * 60 * 1000; // 1 hour
const TOKEN_EXPIRATION_MS = 2 * 60 * 60 * 1000; // 2 hours

export interface SecurityPhraseValidationResult {
  success: boolean;
  error?: string;
  locked?: boolean;
  attemptsRemaining?: number;
}

export class SecurityPhraseService {
  /**
   * Normalize security phrase (case-insensitive, trim whitespace)
   */
  private normalizePhrase(phrase: string): string {
    return phrase.trim().toLowerCase().replace(/\s+/g, ' ');
  }

  /**
   * Validate security phrase format (3 words)
   */
  validatePhraseFormat(phrase: string): { valid: boolean; error?: string } {
    const normalized = this.normalizePhrase(phrase);
    const words = normalized.split(' ').filter(word => word.length > 0);
    
    if (words.length !== 3) {
      return { valid: false, error: 'Security phrase must contain exactly 3 words' };
    }
    
    if (normalized.length < 5) {
      return { valid: false, error: 'Security phrase is too short' };
    }
    
    if (normalized.length > 100) {
      return { valid: false, error: 'Security phrase is too long' };
    }
    
    return { valid: true };
  }

  /**
   * Hash security phrase using SHA-256 (one-way hash)
   */
  async hashPhrase(phrase: string): Promise<string> {
    const normalized = this.normalizePhrase(phrase);
    // Use Web Crypto API for hashing (browser-compatible)
    const encoder = new TextEncoder();
    const data = encoder.encode(normalized);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  }

  /**
   * Verify security phrase against stored hash
   */
  async verifyPhrase(phrase: string, storedHash: string): Promise<boolean> {
    const hash = await this.hashPhrase(phrase);
    return hash === storedHash;
  }

  /**
   * Check if user is locked out due to too many attempts
   */
  async checkLockoutStatus(userId: string): Promise<{ locked: boolean; lockedUntil?: Date }> {
    try {
      const db = getFailoverAwareDatabase();
      const userDoc = await getDoc(doc(db, 'companyUsers', userId));
      
      if (!userDoc.exists()) {
        return { locked: false };
      }
      
      const userData = userDoc.data();
      const lockedUntil = userData.securityPhraseLockedUntil?.toDate();
      
      if (lockedUntil && lockedUntil > new Date()) {
        return { locked: true, lockedUntil };
      }
      
      // Lockout expired, clear it
      if (lockedUntil && lockedUntil <= new Date()) {
        await updateDoc(doc(db, 'companyUsers', userId), {
          securityPhraseAttempts: 0,
          securityPhraseLockedUntil: null
        });
      }
      
      return { locked: false };
    } catch (error) {
      Logger.error('[SecurityPhraseService] Error checking lockout status:', error as Error);
      return { locked: false };
    }
  }

  /**
   * Validate security phrase with rate limiting
   */
  async validateSecurityPhrase(
    userId: string,
    phrase: string
  ): Promise<SecurityPhraseValidationResult> {
    try {
      // Check lockout status
      const lockoutStatus = await this.checkLockoutStatus(userId);
      if (lockoutStatus.locked) {
        return {
          success: false,
          error: `Account locked due to too many failed attempts. Try again after ${lockoutStatus.lockedUntil?.toLocaleString()}`,
          locked: true
        };
      }

      const db = getFailoverAwareDatabase();
      const userDoc = await getDoc(doc(db, 'companyUsers', userId));
      
      if (!userDoc.exists()) {
        return { success: false, error: 'User not found' };
      }
      
      const userData = userDoc.data();
      const storedHash = userData.securityPhraseHash;
      
      if (!storedHash) {
        return { success: false, error: 'Security phrase not set for this account' };
      }
      
      // Verify phrase
      const isValid = await this.verifyPhrase(phrase, storedHash);
      
      if (isValid) {
        // Reset attempts on success
        await updateDoc(doc(db, 'companyUsers', userId), {
          securityPhraseAttempts: 0,
          securityPhraseLockedUntil: null
        });
        
        // Log successful validation
        await this.logSecurityEvent(userId, 'security_phrase_validated', true);
        
        return { success: true };
      } else {
        // Increment failed attempts
        const currentAttempts = (userData.securityPhraseAttempts || 0) + 1;
        const attemptsRemaining = MAX_ATTEMPTS - currentAttempts;
        
        let updateData: any = {
          securityPhraseAttempts: currentAttempts
        };
        
        // Lock account if max attempts reached
        if (currentAttempts >= MAX_ATTEMPTS) {
          const lockedUntil = new Date(Date.now() + LOCKOUT_DURATION_MS);
          updateData.securityPhraseLockedUntil = Timestamp.fromDate(lockedUntil);
        }
        
        await updateDoc(doc(db, 'companyUsers', userId), updateData);
        
        // Log failed validation
        await this.logSecurityEvent(userId, 'security_phrase_validation_failed', false);
        
        if (currentAttempts >= MAX_ATTEMPTS) {
          return {
            success: false,
            error: `Too many failed attempts. Account locked for 1 hour.`,
            locked: true
          };
        }
        
        return {
          success: false,
          error: 'Invalid security phrase',
          attemptsRemaining
        };
      }
    } catch (error) {
      Logger.error('[SecurityPhraseService] Error validating security phrase:', error as Error);
      return { success: false, error: 'An error occurred. Please try again.' };
    }
  }

  /**
   * Set security phrase for user (first time setup)
   */
  async setSecurityPhrase(userId: string, phrase: string): Promise<{ success: boolean; error?: string }> {
    try {
      const formatValidation = this.validatePhraseFormat(phrase);
      if (!formatValidation.valid) {
        return { success: false, error: formatValidation.error };
      }
      
      const hash = await this.hashPhrase(phrase);
      const db = getFailoverAwareDatabase();
      
      await updateDoc(doc(db, 'companyUsers', userId), {
        securityPhraseHash: hash,
        securityPhraseSetAt: Timestamp.now(),
        securityPhraseAttempts: 0,
        securityPhraseLockedUntil: null
      });
      
      // Log security phrase set
      await this.logSecurityEvent(userId, 'security_phrase_set', true);
      
      return { success: true };
    } catch (error) {
      Logger.error('[SecurityPhraseService] Error setting security phrase:', error as Error);
      return { success: false, error: 'Failed to set security phrase. Please try again.' };
    }
  }

  /**
   * Generate token for security phrase change
   */
  async generateChangeToken(userId: string): Promise<{ success: boolean; token?: string; error?: string }> {
    try {
      const db = getFailoverAwareDatabase();
      const userDoc = await getDoc(doc(db, 'companyUsers', userId));
      
      if (!userDoc.exists()) {
        return { success: false, error: 'User not found' };
      }
      
      // Generate secure random token using Web Crypto API
      const array = new Uint8Array(32);
      window.crypto.getRandomValues(array);
      const token = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
      const expiresAt = new Date(Date.now() + TOKEN_EXPIRATION_MS);
      
      await updateDoc(doc(db, 'companyUsers', userId), {
        securityPhraseChangeToken: token,
        securityPhraseChangeExpiresAt: Timestamp.fromDate(expiresAt)
      });
      
      return { success: true, token };
    } catch (error) {
      Logger.error('[SecurityPhraseService] Error generating change token:', error as Error);
      return { success: false, error: 'Failed to generate token. Please try again.' };
    }
  }

  /**
   * Verify change token and get user ID
   * NOTE: For unauthenticated users (password reset), use Cloud Function instead
   * This method is kept for authenticated users but may not work for unauthenticated flows
   */
  async verifyChangeToken(token: string): Promise<{ success: boolean; userId?: string; error?: string }> {
    try {
      // For unauthenticated users, use Cloud Function
      // For authenticated users, can use direct Firestore query
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../config/firebase');
      const verifyTokenCallable = httpsCallable(functions, 'verifySecurityPhraseChangeToken');
      
      const result = await verifyTokenCallable({ token }) as { data: { success: boolean; userId?: string; error?: string } };
      return result.data;
    } catch (error) {
      Logger.error('[SecurityPhraseService] Error verifying change token:', error as Error);
      return { success: false, error: 'An error occurred. Please try again.' };
    }
  }

  /**
   * Change security phrase using token
   * NOTE: For unauthenticated users (password reset), use Cloud Function instead
   * This method is kept for authenticated users but may not work for unauthenticated flows
   */
  async changeSecurityPhraseWithToken(
    userId: string,
    token: string,
    newPhrase: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Use Cloud Function for token-based changes (works for both authenticated and unauthenticated)
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../config/firebase');
      const changePhraseCallable = httpsCallable(functions, 'changeSecurityPhraseWithToken');
      
      const result = await changePhraseCallable({
        userId,
        token,
        newPhrase
      }) as { data: { success: boolean; error?: string } };
      
      return result.data;
    } catch (error) {
      Logger.error('[SecurityPhraseService] Error changing security phrase:', error as Error);
      return { success: false, error: 'Failed to change security phrase. Please try again.' };
    }
  }

  /**
   * Check if user has security phrase set
   */
  async hasSecurityPhrase(userId: string): Promise<boolean> {
    try {
      const db = getFailoverAwareDatabase();
      const userDoc = await getDoc(doc(db, 'companyUsers', userId));
      
      if (!userDoc.exists()) {
        return false;
      }
      
      const userData = userDoc.data();
      return !!userData.securityPhraseHash;
    } catch (error) {
      Logger.error('[SecurityPhraseService] Error checking security phrase:', error as Error);
      return false;
    }
  }

  /**
   * Log security events for audit
   */
  private async logSecurityEvent(
    userId: string,
    eventType: string,
    success: boolean
  ): Promise<void> {
    try {
      const db = getFailoverAwareDatabase();
      const { collection, addDoc } = await import('firebase/firestore');
      
      await addDoc(collection(db, 'securityAuditLog'), {
        userId,
        eventType,
        success,
        timestamp: Timestamp.now(),
        ipAddress: null, // Can be added if available
        userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : null
      });
      
      Logger.info(`[SecurityPhraseService] Security event logged: ${eventType}`, {
        userId,
        success
      });
    } catch (error) {
      Logger.error('[SecurityPhraseService] Error logging security event:', error as Error);
      // Don't throw - logging failures shouldn't break the flow
    }
  }
}

export const securityPhraseService = new SecurityPhraseService();
